import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TechStack = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [hoveredTech, setHoveredTech] = useState(null);

  const techCategories = [
    {
      id: 'frontend',
      name: 'Frontend Arsenal',
      icon: 'Monitor',
      color: 'brand-cyan',
      technologies: [
        { name: 'React 18', level: 95, description: 'Advanced hooks, concurrent features, and performance optimization' },
        { name: 'Next.js', level: 92, description: 'SSR, SSG, API routes, and full-stack development' },
        { name: 'TypeScript', level: 88, description: 'Type-safe development with advanced patterns' },
        { name: 'Three.js', level: 85, description: 'WebGL 3D graphics and immersive experiences' },
        { name: 'Framer Motion', level: 90, description: 'Advanced animations and gesture interactions' },
        { name: 'Tailwind CSS', level: 93, description: 'Utility-first styling with custom design systems' }
      ]
    },
    {
      id: 'backend',
      name: 'Backend Infrastructure',
      icon: 'Server',
      color: 'brand-purple',
      technologies: [
        { name: 'Node.js', level: 90, description: 'Scalable server-side JavaScript applications' },
        { name: 'Python', level: 87, description: 'AI/ML integration and data processing' },
        { name: 'GraphQL', level: 85, description: 'Efficient API design and data fetching' },
        { name: 'PostgreSQL', level: 88, description: 'Advanced database design and optimization' },
        { name: 'Redis', level: 82, description: 'Caching and real-time data management' },
        { name: 'Docker', level: 86, description: 'Containerization and deployment automation' }
      ]
    },
    {
      id: 'cloud',
      name: 'Cloud & DevOps',
      icon: 'Cloud',
      color: 'brand-green',
      technologies: [
        { name: 'AWS', level: 89, description: 'Comprehensive cloud infrastructure and services' },
        { name: 'Kubernetes', level: 83, description: 'Container orchestration and scaling' },
        { name: 'CI/CD', level: 91, description: 'Automated testing and deployment pipelines' },
        { name: 'Terraform', level: 80, description: 'Infrastructure as code and automation' },
        { name: 'Monitoring', level: 87, description: 'Application performance and error tracking' },
        { name: 'Security', level: 85, description: 'Security best practices and compliance' }
      ]
    },
    {
      id: 'emerging',
      name: 'Emerging Tech',
      icon: 'Zap',
      color: 'brand-orange',
      technologies: [
        { name: 'AI/ML', level: 78, description: 'Machine learning integration and automation' },
        { name: 'Blockchain', level: 75, description: 'Decentralized applications and smart contracts' },
        { name: 'WebAssembly', level: 72, description: 'High-performance web applications' },
        { name: 'AR/VR', level: 70, description: 'Immersive web experiences and spatial computing' },
        { name: 'IoT', level: 68, description: 'Internet of Things integration and edge computing' },
        { name: 'Quantum', level: 65, description: 'Quantum computing concepts and applications' }
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % techCategories.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [techCategories.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-brand-cyan rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-morphism cyber-border mb-6">
            <Icon name="Code" size={16} className="text-brand-green" />
            <span className="font-mono text-sm text-brand-green">TECHNOLOGY ARSENAL</span>
          </div>
          
          <h2 className="font-orbitron font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Technical <span className="text-brand-green">Mastery</span>
          </h2>
          
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto">
            A comprehensive arsenal of cutting-edge technologies and frameworks, constantly evolving with the digital frontier.
          </p>
        </motion.div>

        {/* Category Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {techCategories.map((category, index) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(index)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-inter font-medium text-sm transition-all duration-300 cyber-hover ${
                activeCategory === index
                  ? `text-${category.color} bg-${category.color}/10 cyber-border-active cyber-glow`
                  : 'text-text-secondary hover:text-foreground glass-morphism cyber-border hover:cyber-border-active'
              }`}
            >
              <Icon 
                name={category.icon} 
                size={16} 
                className={activeCategory === index ? `text-${category.color}` : ''} 
              />
              <span>{category.name}</span>
            </button>
          ))}
        </motion.div>

        {/* Technology Grid */}
        <motion.div
          key={activeCategory}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {techCategories[activeCategory].technologies.map((tech, index) => (
            <motion.div
              key={tech.name}
              variants={itemVariants}
              className="group relative"
              onMouseEnter={() => setHoveredTech(tech.name)}
              onMouseLeave={() => setHoveredTech(null)}
            >
              <div className="glass-morphism rounded-xl p-6 cyber-border hover:cyber-border-active transition-all duration-300 cyber-hover h-full">
                {/* Tech Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-orbitron font-bold text-lg text-foreground">
                    {tech.name}
                  </h3>
                  <div className={`text-sm font-mono text-${techCategories[activeCategory].color}`}>
                    {tech.level}%
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-surface rounded-full h-2 overflow-hidden">
                    <motion.div
                      className={`h-full bg-gradient-to-r from-${techCategories[activeCategory].color} to-${techCategories[activeCategory].color}/60 rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${tech.level}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                    />
                  </div>
                </div>

                {/* Description */}
                <p className="font-inter text-sm text-text-secondary leading-relaxed">
                  {tech.description}
                </p>

                {/* Hover Effect */}
                <div className={`absolute inset-0 rounded-xl bg-gradient-to-br from-${techCategories[activeCategory].color}/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Category Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center space-x-4 px-6 py-3 rounded-full glass-morphism cyber-border">
            <Icon 
              name={techCategories[activeCategory].icon} 
              size={16} 
              className={`text-${techCategories[activeCategory].color}`} 
            />
            <span className="font-inter text-sm text-foreground">
              Currently viewing: {techCategories[activeCategory].name}
            </span>
            <div className={`w-2 h-2 bg-${techCategories[activeCategory].color} rounded-full animate-pulse`}></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStack;